import { useEffect, useReducer } from 'react';

import { useLocation } from 'react-router-dom';

import {
  spotifyAuthUrl,
  spotifyClientId,
  redirectUri,
  scope,
} from '../../config';

import spotifyClient from './spotifyClient';
import {
  Action,
  State,
  STATUS,
  SET_ERROR,
  SET_USER,
  SET_STATUS,
  SET_TOKEN,
  SET_ARTISTS,
  LOGOUT,
} from './useSpotifyLoginTokenStrategy.types';

const initialState: State = {
  status: STATUS.INITIAL,
  error: null,
  token: null,
  user: null,
  artists: {
    longTerm: null,
    mediumTerm: null,
    shortTerm: null,
  },
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case SET_STATUS:
      return { ...state, status: action.payload };
    case SET_TOKEN:
      return {
        ...state,
        token: action.payload,
      };
    case SET_USER:
      return {
        ...state,
        user: action.payload,
      };
    case SET_ARTISTS:
      return {
        ...state,
        artists: action.payload,
        status: STATUS.LOGGED_IN,
      };
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
        status: STATUS.ERROR,
      };
    case LOGOUT:
      return { ...state, user: null, status: STATUS.INITIAL };
    default:
      return { ...state };
  }
};

export const useSpotifyLoginTokenStrategy = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { hash } = useLocation();

  /**
   *
   */
  useEffect(() => {
    if (hash) {
      dispatch({ type: SET_STATUS, payload: STATUS.LOADING });
      let token = null;

      try {
        token = getSpotifyTokenHash(hash);
      } catch (error) {
        if (error instanceof InvalidHashError) {
          return dispatch({ type: SET_ERROR, payload: error.message });
        } else {
          return dispatch({ type: SET_ERROR, payload: error.message });
        }
      }

      dispatch({ type: SET_TOKEN, payload: token });
    }
  }, [hash]);

  /**
   *
   */
  useEffect(() => {
    const getSpotifyData = async (token: string) => {
      const client = spotifyClient(token);

      try {
        const me = await client.me();

        dispatch({ type: SET_USER, payload: me });
      } catch (error) {
        if (error.isAxiosError) {
          return dispatch({
            type: SET_ERROR,
            payload: error.response.data.error.message,
          });
        } else {
          return dispatch({
            type: SET_ERROR,
            payload: error.message,
          });
        }
      }

      try {
        const shortTerm = await client.top('artists', 'short_term');
        const mediumTerm = await client.top('artists', 'medium_term');
        const longTerm = await client.top('artists', 'long_term');

        dispatch({
          type: SET_ARTISTS,
          payload: { shortTerm, mediumTerm, longTerm },
        });
      } catch (error) {
        if (error.isAxiosError) {
          return dispatch({
            type: SET_ERROR,
            payload: error.response.data.error.message,
          });
        } else {
          return dispatch({
            type: SET_ERROR,
            payload: error.message,
          });
        }
      }
    };

    if (state.token) {
      getSpotifyData(state.token);
    }
  }, [state.token]);

  const login = () => {
    // @ts-ignore
    window.location = `${spotifyAuthUrl}?client_id=${spotifyClientId}&redirect_uri=${redirectUri}&response_type=token&show_dialog=true&scope=${scope}`;
  };

  const noLogin = () => {
    dispatch({ type: SET_STATUS, payload: STATUS.NO_LOGIN });
  };

  const logout = () => {
    if (state.status === STATUS.NO_LOGIN) {
      dispatch({ type: LOGOUT });
    } else {
      // @ts-ignore
      window.location = '';
    }
  };

  return {
    ...state,
    actions: {
      login,
      noLogin,
      logout,
    },
  };
};

/**
 *
 */
const getSpotifyTokenHash = (hash: string) => {
  if (!hash || hash.length < 1) {
    return null;
  }

  const split = hash?.split('&');

  if (!Array.isArray(split) || split.length !== 3) {
    throw new InvalidHashError('invalid hash format');
  }

  const tokenString = split[0];

  if (!tokenString) {
    throw new InvalidHashError('invalid hash format');
  }

  const splittedTokenString = tokenString.split('=');

  if (!Array.isArray(splittedTokenString) || splittedTokenString.length !== 2) {
    throw new InvalidHashError('invalid hash format');
  }

  return splittedTokenString[1];
};

class InvalidHashError extends Error {
  constructor(message: string) {
    super(message); // (1)
    this.name = 'ValidationError'; // (2)
  }
}
